import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { lazy, Suspense, useState, useCallback, useEffect } from "react";
import SplashScreen from "./components/SplashScreen";
import MaintenancePage from "./pages/MaintenancePage";
import { MAINTENANCE_MODE_ENABLED } from "./config/maintenance";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import SupportPage from "./pages/SupportPage";
import MapPage from "./pages/MapPage";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import PaymentPage from "./pages/PaymentPage";
import AddPaymentPage from "./pages/AddPaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import AudioPlayerPage from "./pages/AudioPlayerPage";
import TourPlannerPage from "./pages/TourPlannerPage";
import AuthCallback from "./pages/AuthCallback";
import {
  PRIVACY_POLICY_MARKDOWN,
  TERMS_OF_USE_MARKDOWN,
} from "./config/legalContent";

const LegalPage = lazy(() => import("./pages/LegalPage"));

const LegalPageRoute = (props) => (
  <Suspense
    fallback={
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading document...
      </div>
    }
  >
    <LegalPage {...props} />
  </Suspense>
);

// Scroll to top on route change (except map page which manages its own scroll)
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith("/explore")) {
      // Override CSS scroll-behavior: smooth for instant reset
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = '';
      });
    }
  }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnTo)}`} replace />;
  }
  
  return children;
};

// Public Route (redirect to map if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    const requestedRedirect = new URLSearchParams(location.search).get("redirect") || "/explore";
    const redirectTo = requestedRedirect.startsWith("/") && !requestedRedirect.startsWith("//")
      ? requestedRedirect
      : "/explore";
    return <Navigate to={redirectTo} replace />;
  }
  
  return children;
};

// Intercept #session_id hash fragment from Google OAuth
const HashRedirect = () => {
  const location = useLocation();
  const hash = location.hash || window.location.hash;
  if (hash && hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return null;
};

function WebApplication() {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const [splashDone, setSplashDone] = useState(!isMobile);
  const handleSplashFinished = useCallback(() => setSplashDone(true), []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          {!splashDone && <SplashScreen onFinished={handleSplashFinished} />}
          <div className="min-h-screen bg-background">
            <HashRedirect />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/login" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/signup" element={
                <PublicRoute>
                  <SignupPage />
                </PublicRoute>
              } />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/account-deletion" element={<AccountDeletionPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route
                path="/privacy"
                element={
                  <LegalPageRoute
                    title="Privacy Policy"
                    markdown={PRIVACY_POLICY_MARKDOWN}
                    testId="privacy-policy-page"
                  />
                }
              />
              <Route
                path="/terms"
                element={
                  <LegalPageRoute
                    title="Terms of Use"
                    markdown={TERMS_OF_USE_MARKDOWN}
                    testId="terms-of-use-page"
                  />
                }
              />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
              
              {/* Public Map - Anonymous browsing allowed */}
              <Route path="/explore" element={<MapPage />} />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/pricing" element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              } />
              <Route path="/add-payment" element={
                <ProtectedRoute>
                  <AddPaymentPage />
                </ProtectedRoute>
              } />
              <Route path="/payment/success" element={
                <ProtectedRoute>
                  <PaymentSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/payment/cancel" element={
                <ProtectedRoute>
                  <PaymentCancelPage />
                </ProtectedRoute>
              } />
              <Route path="/player/:contentId" element={
                <ProtectedRoute>
                  <AudioPlayerPage />
                </ProtectedRoute>
              } />
              <Route path="/plan-tour" element={
                <ProtectedRoute>
                  <TourPlannerPage />
                </ProtectedRoute>
              } />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

function App() {
  if (MAINTENANCE_MODE_ENABLED) {
    return <MaintenancePage />;
  }

  return <WebApplication />;
}

export default App;
